import React, { useState, useRef } from 'react';
import { Upload, X, Link2, Image, Loader2 } from 'lucide-react';
import { uploadApi } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ImageUpload = ({ 
  value, 
  onChange, 
  label = "Image",
  placeholder = "https://...",
  showPreview = true,
  previewHeight = "h-32",
  accept = "image/*"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(!value || value.startsWith('http'));
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploading(true);

    try {
      const response = await uploadApi.upload(file);
      if (response.data.success) {
        // Construct full URL for the uploaded file
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        onChange(fullUrl);
        setShowUrlInput(false);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');
    setShowUrlInput(true);
  };

  const toggleInputMode = () => {
    setShowUrlInput(!showUrlInput);
  };

  const isUploadedFile = value && value.includes('/uploads/');

  return (
    <div className="space-y-2" data-testid="image-upload">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <button
          type="button"
          onClick={toggleInputMode}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          {showUrlInput ? (
            <><Upload className="h-3 w-3" /> Upload file</>
          ) : (
            <><Link2 className="h-3 w-3" /> Use URL</>
          )}
        </button>
      </div>

      {showUrlInput ? (
        <div className="space-y-2">
          <Input
            value={value || ''}
            onChange={handleUrlChange}
            placeholder={placeholder}
            data-testid="image-url-input"
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            {isUploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                <span className="text-sm text-gray-500 mt-2">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-8 w-8 text-gray-400" />
                <span className="text-sm text-gray-500 mt-2">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  PNG, JPG, GIF, WebP up to 10MB
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {showPreview && value && (
        <div className={`relative ${previewHeight} bg-gray-100 rounded-lg overflow-hidden group`}>
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-full h-full items-center justify-center text-gray-400 hidden">
            <Image className="h-8 w-8" />
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove image"
          >
            <X className="h-4 w-4" />
          </button>
          {isUploadedFile && (
            <span className="absolute bottom-2 left-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded">
              Uploaded
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
